import type { TranslationTree } from "./types.js";

export const en = {
  app: {
    title: "Xiuxian IO",
    shortTitle: "Xiuxian",
    runtimeTitle: "Xiuxian IO Runtime",
  },
  identity: {
    label: "Name",
    placeholder: "Name",
    start: "Enter the Path",
    restart: "Reincarnate",
    nameLengthError: "Name must be 1-{max} characters.",
    scoreSummary: "Run score {score}",
    devLevel: "Dev starting level",
    contentLabel: "Content pages",
    libraryLink: "Archive",
    privacyLink: "Privacy",
    feedbackLink: "Feedback",
    discordLink: "Discord",
    appearance: "Outfit",
    skinCardLabel: "Character",
    roomCardLabel: "Room",
    appearanceTitle: "Choose Your Cultivator",
    appearanceSubtitle: "Pick a cultivator and begin your ascension.",
    appearanceAction: "Change",
    appearanceSelected: "Picked",
    appearanceDone: "Confirm Look",
    skin: "Cultivator",
    skinStageShape: "In-Game Form",
    terrain: "Ground Theme",
    terrainGrid: "Meridian Grid",
    terrainHex: "Honeycomb Field",
    terrainDot: "Star Sand",
  },
  room: {
    createRoom: "Create Private Room",
    createConfirm: "Create Room",
    joinRoom: "Join",
    joinPrimary: "Join room {code}",
    joinSectionTitle: "Join by invite code",
    orLabel: "or",
    invitePlaceholder: "Invite code",
    inviteError: "Please enter a valid invite code",
    configMapLabel: "Map",
    configLimitLabel: "Players",
    configBotLabel: "Bots",
    errorMapTooSmall:
      "This map can't hold that many; lower the cap or pick a larger map",
    errorTooManyBots: "Bot count must be below the player cap",
    waitingTitle: "Private Room Lobby",
    inviteLabel: "Invite code",
    copyInvite: "Copy invite link",
    copied: "Invite link copied",
    copyFailed: "Copy failed, please copy manually",
    startMatch: "Start Game",
    startNeedsPlayers: "Need 2+ players",
    leave: "Leave",
    ownerHint: "You are the host. Invite friends, then start.",
    guestHint: "Waiting for the host to start…",
    playersTitle: "Members ({count}/{limit})",
    ownerTag: "Host",
    botTag: "BOT",
    configSummary: "{mapSize} · Up to {limit} · {mode}",
    mapSmall: "Small arena",
    mapMedium: "Medium arena",
    mapLarge: "Large arena",
    modeSolo: "Free-for-all",
    modeTeam: "Team battle",
    matchPanelTitle: "This Match",
    countdownLabel: "{time} left",
    respawnCountdown: "Fallen — reviving in {seconds}s…",
    standingsTitle: "Standings",
    resultTitle: "Match Over",
    winnerLabel: "Winner: {name}",
    noWinner: "No winner",
    backToEntry: "Back to Home",
    spectating: "Spectating: {name}",
    spectateNone: "No one to spectate",
    unavailableTitle: "Can't join room",
    endedMessage: "This room's match has ended, or the room no longer exists.",
    fullMessage: "The room is full.",
    invalidConfigMessage: "Invalid room configuration.",
    backHome: "Back to Home",
    resetInvite: "Reset invite code",
    kick: "Kick",
    swapTeam: "Tap to switch side",
    rematchButton: "Rematch",
    chatPlaceholder: "Say something…",
    chatSend: "Send",
    configModeLabel: "Mode",
    winLastSurvivor: "Last survivor",
    winTimedScore: "Timed high score",
    winTeamTimedScore: "2-team timed score",
    teamAzure: "Azure",
    teamCrimson: "Crimson",
    teamWinner: "{team} wins",
  },
  settings: {
    title: "Settings",
    language: "Language",
    sound: "Sound",
    fullscreen: "Fullscreen",
    close: "Close",
    room: "Room",
    appearance: "Outfit",
    library: "Archive",
    privacy: "Privacy",
    feedback: "Feedback",
    discord: "Discord",
  },
  leaderboard: {
    title: "Rankings",
    empty: "Waiting for cultivators",
    mobileToggle: "Rank",
    defaultPlayerName: "Unnamed cultivator",
    periodLabel: {
      day: "Day",
      week: "Week",
      month: "Month",
    },
    periodTitle: {
      day: "Top 10 Today",
      week: "Top 10 This Week",
      month: "Top 10 This Month",
    },
  },
  controls: {
    mobileBoost: "Dash",
    soundOn: "Sound On",
    soundOff: "Sound Off",
    mouseLockOff: "Mouse Lock Off",
    mouseLockOn: "Mouse Locked",
    mouseLockTitleOff: "Click to lock the mouse",
    mouseLockTitleOn: "Press Esc to unlock the mouse",
  },
  pwa: {
    install: "Install",
    fullscreen: "Fullscreen",
    exitFullscreen: "Exit Fullscreen",
    dismissLabel: "Dismiss tip",
    installTip: "Install to your home screen for a cleaner fullscreen run.",
    manualTip: "Add to your home screen for a more fullscreen experience.",
  },
  locale: {
    label: "Language",
    zhCN: "中文",
    en: "English",
  },
  realm: {
    qi: "Qi Refining",
    foundation: "Foundation Establishment",
    core: "Core Formation",
    nascent: "Nascent Soul",
    spirit: "Spirit Transformation",
    layer: "{realm} Layer {layer}",
    qiShort: "Qi",
    foundationShort: "Foundation",
    coreShort: "Core",
    nascentShort: "Nascent",
    spiritShort: "Spirit",
    layerCompact: "{realm} {layer}",
    ascension: "Ascension",
  },
  treasure: {
    qiankun_ruler: {
      name: "Cosmos Ruler",
      shortEffect: "Greatly expands formation range",
    },
    hunyuan_fan: {
      name: "Hunyuan Fan",
      shortEffect: "Widens the attack fan",
    },
    spirit_gourd: {
      name: "Spirit Gourd",
      shortEffect: "Triples spirit absorption",
    },
    thunder_sword_box: {
      name: "Thunder Sword Box",
      shortEffect: "Launches flying swords faster",
    },
    myriad_sword_token: {
      name: "Myriad Sword Token",
      shortEffect: "Slashes two targets at once",
    },
    wind_thunder_boots: {
      name: "Wind-Thunder Boots",
      shortEffect: "Improves movement; sprint costs no stamina",
    },
    black_tortoise_armor: {
      name: "Black Tortoise Armor",
      shortEffect: "Prevents death 3 times",
    },
    ascension_pearl: {
      name: "Ascension Pearl",
      shortEffect: "Not yet available",
    },
  },
  runtime: {
    fallbackCultivator: "A cultivator",
    status: {
      default: "Waiting for the realtime connection.",
      entryReady: "Confirm your name to enter the realm.",
      bootstrapFailed: "Failed to prepare identity or establish connection.",
      restartPrompt: "You have fallen. Confirm your name to reincarnate.",
      restartFailed: "Failed to prepare identity for reincarnation.",
      connecting: "Connecting to the spirit realm…",
      websocketOpen: "WebSocket opened. Sending handshake…",
      transportError: "Connection error. Waiting for close or reconnect.",
      disconnectedRetry: "Spirit realm link lost. Retrying shortly.",
      manualDisconnect: "Client closed the connection.",
      reconnecting: "Re-entering the spirit realm…",
      protocolParseFailed: "Failed to parse server protocol.",
      joinedRoom: "Joined room {roomId}. Waiting for world sync.",
      syncedTick: "Synced world tick {tick}.",
      deadRestart: "You have fallen. Confirm your name to reincarnate.",
      spectatingKiller:
        "You have fallen. Click or press any key to reincarnate.",
      eventReceived: "Received event: {event}",
      serverErrorReturned: "Server returned an error message.",
    },
    error: {
      transport: "Network transport error.",
      nonTextMessage: "Received a non-text protocol message.",
    },
    log: {
      started: "Runtime started. Waiting for connection.",
      connect: "Connecting {url}",
      helloSent: "Connected. Sending client_hello.",
      transportError: "Connection transport error.",
      close: "Connection closed: {reason}",
      respawnRequested: "Respawn requested.",
      nonTextIgnored: "Non-text protocol message ignored.",
      protocolError: "Protocol error: {detail}",
      handshake: "Handshake complete: player={playerId} room={roomId}",
      reconnectIn: "Retrying in {seconds}s.",
      serverError: "Server error: {code}",
      serviceWorkerFailed:
        "Service worker registration failed. Continuing as a normal web page.",
      pwaStorageUnavailable:
        "Local storage is unavailable; the home-screen tip setting is temporary.",
      playerKeyStorageUnavailable:
        "Local storage is unavailable; the player identity is temporary.",
      identityStorageUnavailable:
        "Local storage is unavailable; this identity is temporary.",
      audioStorageUnavailable:
        "Local storage is unavailable; this sound setting is temporary.",
      leaderboardStorageUnavailable:
        "Local storage is unavailable; this rankings selection is temporary.",
      nameConfirmed: "Name confirmed: {name}",
      restartConfirmed: "Reincarnating: {name}",
      startupFailed: "Startup failed: {detail}",
      restartFailed: "Reincarnation failed: {detail}",
    },
    event: {
      attackStarted: "Windup: {attackerId} -> {targetId}",
      attackHit: "Hit: {attackerId} -> {targetId}",
      attackMissed: "Miss: {attackerId} -> {targetId}",
      pickup: "Absorb: {playerId} / {value} spirit",
      death: "Fallen: {playerId}{killer}{dropped}",
      deathKiller: " / killer {killerId}",
      deathDropped: " / dropped {count} spirit",
      drop: "Burst: {playerId} / {count} spirit",
      respawn: "Respawn: {playerId}",
      ascension: "Ascension: {playerId} / {spiritPower} spirit",
      ascensionStarted: "Ascension ritual: {name} / +{scoreGain} score",
      treasureSpawned: "Treasure spawned: {kind}",
      treasurePicked: "Treasure claimed: {kind}",
      treasureGuard:
        "Black Tortoise guard: {playerId} / {remainingCharges} charges left",
      worldReborn: "World reborn: {ascenderName} returned",
      serverEvent: "Server event: {event}",
    },
    roomEvent: {
      playerJoined: "{name} joined the room",
      playerLeft: "{name} left the room",
      ownerTransferred: "Host transferred to {ownerName}",
      roomStarted: "The host started the match",
      roomClosed: "The room was closed",
      matchEnded: "Match over: winner {winnerName}",
      playerKicked: "The host removed you from the room",
      inviteReset: "Invite code reset",
      rematch: "The host started a rematch",
    },
    treasureNotice: {
      spawnTitle: "Treasure Emerges",
      pickupTitle: "Treasure Acquired",
      guardTitle: "Black Tortoise Guard",
      guardSelfBody:
        "Blocked a fatal blow for you. {remainingCharges} charges left.",
      guardOtherBody:
        "Blocked a flying sword. {remainingCharges} charges left.",
      ascensionTitle: "Ascension",
      ascensionBody: "{name} opens the heavenly gate. The world pauses.",
      iconText: "Asc",
    },
    ascension: {
      selfTitle: "You Ascended",
      otherTitle: "{name} Ascended",
      selfSubtitle: "Ascension score +{scoreGain}, total {totalScore}",
      otherSubtitle:
        "The heavenly gate opens; all cultivators pause to witness it.",
      seal: "Ascend",
      countdownUnit: "s",
      rebornSeal: "Reborn",
      rebornTitle: "The World Turns Again",
      rebornSubtitle: "{name} returns with score preserved. The run continues.",
      announcementNoScore: "{name} ascended and opened the heavenly gate.",
      announcementWithScore: "{name} ascended with total score {totalScore}.",
      worldRebornAnnouncement: "{name} ascended; the world is reborn.",
    },
    hint: {
      mobile: "Left joystick to move · Hold the right dash button to boost",
      desktop:
        "Point with mouse to move · Left click/Shift to dash · M locks mouse · Esc unlocks",
    },
    debug: {
      formationLayers: "Perf toggle: formation texture layers {count}",
      background: "Perf toggle: background {mode}",
      stones: "Perf toggle: spirit stones {mode}",
      minimap: "Perf toggle: minimap {mode}",
      playerSprites: "Perf toggle: player sprites {mode}",
      simplified: "simplified",
      full: "full",
      shown: "shown",
      hidden: "hidden",
    },
  },
} as const satisfies TranslationTree;
